import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Dialog,
  DialogContent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import PrintIcon from '@mui/icons-material/Print';
import { AlertMessages } from '../AlertMessages';
import { getWalletEmployees, getEmployeeMonthlyReport } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/formatCoins';
import { FormContainer } from './styles';
import { AuthorizationTerm } from './AuthorizationTerm';
import logoImage from '../../assets/a-logo.svg';

export const EmployeePurchasesForm = ({ employeeId: initialEmployeeId = null }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(initialEmployeeId || '');
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [showAuthorizationTerm, setShowAuthorizationTerm] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  // Carregar lista de colaboradores
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const data = await getWalletEmployees();
        setEmployees(data.employees || []);
        
        // Se employeeId inicial foi fornecido, selecionar automaticamente
        if (initialEmployeeId && data.employees) {
          const found = data.employees.find(emp => emp.id === parseInt(initialEmployeeId, 10));
          if (found) {
            setSelectedEmployeeId(initialEmployeeId);
          }
        }
      } catch (error) {
        setAlertState({
          show: true,
          type: 'error',
          message: getErrorMessage(error),
        });
      }
    };

    loadEmployees();
  }, [initialEmployeeId]);

  const handleGenerateReport = async () => {
    if (!selectedEmployeeId) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Selecione um colaborador',
      });
      return;
    }

    if (!startDate || !endDate || startDate.isAfter(endDate)) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Selecione um período válido (data início menor ou igual à data fim)',
      });
      return;
    }

    try {
      setLoading(true);
      const data = await getEmployeeMonthlyReport(selectedEmployeeId, {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      });
      setReport(data);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const selectedEmployee = employees.find(emp => emp.id === parseInt(selectedEmployeeId, 10));

  const handlePrintAllEmployees = async () => {
    if (employees.length === 0) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Nenhum colaborador encontrado',
      });
      return;
    }

    try {
      setPrintingAll(true);
      
      // Gerar relatório para cada colaborador e imprimir
      for (const employee of employees) {
        try {
          const data = await getEmployeeMonthlyReport(employee.id, {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
          });
          if (data && data.summary.totalMonth > 0) {
            // Criar um iframe temporário para impressão
            const printWindow = window.open('', '_blank');
            if (printWindow) {
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Termo de Autorização - ${employee.name}</title>
                    <style>body { margin: 0; font-family: Arial, sans-serif; }</style>
                  </head>
                  <body>
                    ${generateAuthorizationTermHTML(employee, data)}
                  </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.onload = () => {
                setTimeout(() => {
                  printWindow.print();
                  setTimeout(() => printWindow.close(), 1000);
                }, 500);
              };
            }
            // Aguardar um pouco entre cada impressão
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (error) {
          console.error(`Erro ao gerar termo para ${employee.name}:`, error);
        }
      }
      
      setAlertState({
        show: true,
        type: 'success',
        message: 'Termos de autorização gerados com sucesso',
      });
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setPrintingAll(false);
    }
  };

  const generateAuthorizationTermHTML = (employee, report) => {
    const purchasesByDate = {};
    if (report && report.orders) {
      report.orders.forEach((order) => {
        const dateKey = new Date(order.date).toLocaleDateString('pt-BR');
        if (!purchasesByDate[dateKey]) {
          purchasesByDate[dateKey] = 0;
        }
        purchasesByDate[dateKey] += parseFloat(order.totalCoins);
      });
    }
    const now = new Date();
    const currentDay = now.getDate();
    const currentMonth = now.toLocaleString('pt-BR', { month: 'long' });
    const currentYear = now.getFullYear();
    const periodStart = report?.period?.startDate
      ? dayjs(report.period.startDate).format('DD/MM/YYYY')
      : '-';
    const periodEnd = report?.period?.endDate
      ? dayjs(report.period.endDate).format('DD/MM/YYYY')
      : '-';

    const formatCurrencyHTML = (value) => {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const discrRows = Object.entries(purchasesByDate)
      .map(([date, amount]) => ({ date, amount }))
      .sort((a, b) => {
        const [da, ma, aa] = a.date.split('/').map(Number);
        const [db, mb, ab] = b.date.split('/').map(Number);
        return new Date(aa, ma - 1, da) - new Date(ab, mb - 1, db);
      });

    const half = Math.ceil(discrRows.length / 2);
    const leftRows = discrRows.slice(0, half);
    const rightRows = discrRows.slice(half);

    const tableHtml = (rows) => `
      <table style="width:100%; border-collapse: collapse; border:1px solid #000;">
        <thead>
          <tr style="border-bottom:2px solid #000; font-weight:bold; font-size:10px;">
            <th style="border:1px solid #000; padding:3px; text-align:center;">DATA</th>
            <th style="border:1px solid #000; padding:3px; text-align:center;">R$</th>
          </tr>
        </thead>
        <tbody>
          ${(rows.length ? rows : [{ date: '', amount: null }]).map((row) => `
            <tr style="height:18px;">
              <td style="border:1px solid #000; padding:2px; text-align:center; font-size:10px;">${row.date || '-'}</td>
              <td style="border:1px solid #000; padding:2px; text-align:center; font-size:10px;">${row.amount != null ? formatCurrencyHTML(row.amount) : ''}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    return `
      <div style="max-width: 210mm; margin: 0 auto; padding: 10mm; page-break-inside: avoid;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 12px; border-bottom: 2px solid #000; padding-bottom: 6px;">
          <div style="font-size: 12px; font-weight: bold;">Total: ${formatCurrencyHTML(report?.summary?.totalMonth || 0)}</div>
          <div style="font-size: 12px; font-weight: bold;">Período: ${periodStart} até ${periodEnd}</div>
        </div>
        
        <div style="text-align: center; margin-bottom: 16px;">
          <div style="display: flex; justify-content: center; margin-bottom: 8px;">
            <img src="${logoImage}" alt="Colégio Autêntico" style="max-width: 180px; height: auto;" />
          </div>
          <h3 style="font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 8px;">
            TERMO DE AUTORIZAÇÃO PARA DESCONTO EM FOLHA DE PAGAMENTO
          </h3>
        </div>
        
        <div style="line-height: 1.6; margin-bottom: 16px; text-align: justify; font-size: 11px;">
          <p style="margin-bottom: 10px;">
            Pelo presente instrumento, eu <span style="border-bottom: 1px solid #000; padding: 2px 4px; font-weight: bold; min-width: 260px; display: inline-block; text-align: center; font-size: 11px;">${employee?.name || '_________________________'}</span>,
            inscrito (a) no CPF sob o nº <span style="border-bottom: 1px solid #000; padding: 2px 4px; font-weight: bold; min-width: 130px; display: inline-block; text-align: center; font-size: 11px;">${employee?.cpf || '________________'}</span>, como funcionário(a) do Colégio Autêntico, autorizo a referida instituição a descontar mensalmente de minha folha de pagamento os valores referentes a consumos realizados por mim na cantina interna da empresa conforme discriminado abaixo, podendo tais verbas inclusive serem descontadas na minha rescisão.
            <br /><br />
            Esta autorização tem validade até que eu solicite o cancelamento da mesma através de solicitação feita de próprio punho e protocolada junto ao setor Recursos Humanos do Colégio Autêntico.
            <br /><br />
            Confirmo ter realizado o consumo junto à cantina da empresa no período de <strong>${periodStart}</strong> até <strong>${periodEnd}</strong>, totalizando <strong>${formatCurrencyHTML(report?.summary?.totalMonth || 0)}</strong>, e declaro ainda estar ciente de que tal valor será debitado em meu próximo salário.
          </p>
          <p style="text-align: right; margin-top: 10px;">
            Campina Grande - PB, <span style="border-bottom: 1px solid #000; padding: 2px 3px; min-width: 35px; display: inline-block; text-align: center;">${currentDay}</span>
            de <span style="border-bottom: 1px solid #000; padding: 2px 3px; min-width: 110px; display: inline-block; text-align: center;">${currentMonth}</span>
            de <span style="border-bottom: 1px solid #000; padding: 2px 3px; min-width: 55px; display: inline-block; text-align: center;">${currentYear}</span>.
          </p>
        </div>
        
        <div style="margin-bottom: 16px;">
          <h4 style="text-align: center; font-weight: bold; margin-bottom: 8px; font-size: 12px;">DISCRIMINAÇÃO DAS COMPRAS</h4>
          <div style="display: flex; gap: 3px;">
            <div style="flex: 1;">${tableHtml(leftRows)}</div>
            <div style="flex: 1;">${tableHtml(rightRows)}</div>
          </div>
        </div>
        
        <div style="margin-top: 56px; text-align: center;">
          <div style="border-top: 1px solid #000; width: 260px; margin: 0 auto; padding-top: 6px;">
            <p style="font-size: 11px; margin: 0; font-weight: 500;">${employee?.name || '_________________________'}</p>
            <p style="font-size: 9px; color: #666; margin: 4px 0 0 0;">Assinatura do Colaborador</p>
          </div>
        </div>
      </div>
    `;
  };

  return (
    <>
      <FormContainer>
        <Typography variant="h5" sx={{ color: '#0b4f6c', fontWeight: 700, mb: 3 }}>
          Relatório de Compras de Colaboradores
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Colaborador</InputLabel>
            <Select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              label="Colaborador"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Selecione um colaborador</em>
              </MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name} {emp.cpf ? `(${emp.cpf})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <DatePicker
                label="Data início"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                inputFormat="DD/MM/YYYY"
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ minWidth: 170 }} />
                )}
              />
              <DatePicker
                label="Data fim"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                inputFormat="DD/MM/YYYY"
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ minWidth: 170 }} />
                )}
              />

            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={loading || !selectedEmployeeId || !startDate || !endDate || startDate.isAfter(endDate)}
              sx={{
                bgcolor: '#1e958c',
                '&:hover': { bgcolor: '#0b4f6c' },
                minWidth: 150,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Gerar Relatório'}
            </Button>
            
            {report && (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => setShowAuthorizationTerm(true)}
                disabled={loading}
                sx={{
                  borderColor: '#1e958c',
                  color: '#1e958c',
                  '&:hover': {
                    borderColor: '#0b4f6c',
                    bgcolor: 'rgba(30, 149, 140, 0.1)',
                  },
                  minWidth: 180,
                }}
              >
                Imprimir Termo
              </Button>
            )}
            
              <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintAllEmployees}
              disabled={loading || printingAll || employees.length === 0}
              sx={{
                borderColor: '#1e958c',
                color: '#1e958c',
                '&:hover': {
                  borderColor: '#0b4f6c',
                  bgcolor: 'rgba(30, 149, 140, 0.1)',
                },
                minWidth: 220,
              }}
            >
              {printingAll ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Gerando...
                </>
              ) : (
                'Imprimir Todos'
              )}
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>

        {report && (
          <>
            {/* Resumo */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Colaborador
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {report.employee.name}
                    </Typography>
                    {report.employee.cpf && (
                      <Typography variant="caption" color="textSecondary">
                        CPF: {report.employee.cpf}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d' }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Total do Período
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#e65100' }}>
                      {formatCurrency(report.summary.totalMonth || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.summary.totalOrders || 0} {report.summary.totalOrders === 1 ? 'compra' : 'compras'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Período */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary">
                Período: {dayjs(report.period.startDate).format('DD/MM/YYYY')} até {dayjs(report.period.endDate).format('DD/MM/YYYY')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {dayjs(report.period.startDate).format('DD/MM/YYYY')} a{' '}
                {dayjs(report.period.endDate).format('DD/MM/YYYY')}
              </Typography>
            </Box>

            {/* Lista de Compras */}
            {report.orders && report.orders.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Data/Hora</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Itens</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{formatDate(order.date)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {order.items.map((item, idx) => (
                              <Typography key={idx} variant="body2">
                                {item.productName} × {item.quantity} = {formatCurrency(item.lineTotal)}
                              </Typography>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(order.totalCoins)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                Nenhuma compra encontrada para este colaborador no período selecionado.
              </Alert>
            )}
          </>
        )}
      </FormContainer>

      {/* Dialog para exibir o termo de autorização */}
      <Dialog
        open={showAuthorizationTerm}
        onClose={() => setShowAuthorizationTerm(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent>
          {report && selectedEmployee && (
            <AuthorizationTerm
              employee={selectedEmployee}
              report={report}
              onClose={() => setShowAuthorizationTerm(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
